import { useEffect } from "react";
import { Dispatch, SetStateAction, useState } from "react";
import { SearchType } from "Dashboard/domain/SearchType";
import { setSearchInputPopover } from "../inputSlice";
import { useAppDispatch } from "hooks/hooks";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesCB from "../../../styles/popover.module.scss";
import { IGetConcreteBaseInputsRequestParams } from "Dashboard/scenes/ConcreteBase/domain/IGetConcreteBaseInputsRequestParams";
import RepositorySelect from "./RepositorySelect";
import { styles } from "components/Popover/popoverSelectStyle";
import UnitSelect from "Dashboard/components/UnitSelect";

interface IProps {
  handleSubmit: UseFormHandleSubmit<IGetConcreteBaseInputsRequestParams>;
  register: UseFormRegister<IGetConcreteBaseInputsRequestParams>;
  control: Control<IGetConcreteBaseInputsRequestParams>;
  reset: UseFormReset<IGetConcreteBaseInputsRequestParams>;
  setFocus: UseFormSetFocus<IGetConcreteBaseInputsRequestParams>;
  searchType: SearchType;
  setSearchType: React.Dispatch<React.SetStateAction<SearchType>>;
  getValues: UseFormGetValues<IGetConcreteBaseInputsRequestParams>;
  setValue: UseFormSetValue<IGetConcreteBaseInputsRequestParams>;
  setGetInputsRequestParams: Dispatch<SetStateAction<IGetConcreteBaseInputsRequestParams>>;
};

const SearchPopover = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { register, control, handleSubmit, getValues, setValue, setFocus, setGetInputsRequestParams } = props;

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(
    [getValues("dateFrom") ? new Date(getValues("dateFrom")!) : null,
    getValues("dateTo") ? new Date(getValues("dateTo")!) : null]);

  const switchSearchMode = (searchType: SearchType) => {
    props.setSearchType(searchType);
  };

  const onSubmit = async (data: IGetConcreteBaseInputsRequestParams) => {
    try {
      if (props.searchType === SearchType.BASIC) {
        data = { searchTerm: data.searchTerm };
      } else {
        data.searchTerm = undefined;
      }
      setGetInputsRequestParams(data);
      dispatch(setSearchInputPopover({ isOpen: false }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (props.searchType === SearchType.BASIC) {
      setFocus("searchTerm");
    } else {
      setFocus("receiptNumber");
    }
  }, [setFocus, props.searchType]);

  return (
    <form
      className={`${classesCB.searchInputPopover} ${classes.basicPopover}`}
      onSubmit={handleSubmit(onSubmit)}
    >
      {props.searchType === SearchType.BASIC && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.colDir}`}>
              <input
                type="text" className={`${classes.itemBorder} ${classes.fullWidth}`}
                {...register("searchTerm", { required: false })}
              />
            </div>
            <div className={classes.gPopoverDisclaimer}>
              * možete uneti više reči tj. kriterijuma pretrage
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.ADVANCED)}
                className={classes.clickableText}
              >
                NAPREDNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
      {props.searchType === SearchType.ADVANCED && (
        <>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. računa</label>
              <input type="text" className={classes.itemBorder}
                {...register("receiptNumber", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>br. otpremnice</label>
              <input type="text" className={classes.itemBorder}
                {...register("billOfLandingId", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>šifra artikla</label>
              <input type="text" className={classes.itemBorder}
                {...register("code", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>magacin</label>
              <Controller
                name="repositoryId"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <RepositorySelect
                    value={getValues("repositoryId")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>
          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>naziv</label>
              <input type="text" className={classes.itemBorder}
                {...register("name", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>dobavljač</label>
              <input type="text" className={classes.itemBorder}
                {...register("supplier", { required: false })}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>količina</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountTotalRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>mera</label>
              <Controller
                name="quantityUnit"
                control={control}
                defaultValue={undefined}
                rules={{
                  required: false,
                }}
                render={({ field: { onChange } }) => (
                  <UnitSelect
                    value={getValues("quantityUnit")}
                    onChange={onChange}
                    customStyles={styles.customStyleClassic}
                  />
                )}
              />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>cena po kom.</label>
              <input type="text" className={classes.itemBorder}
                {...register("pricePerUnitRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>cena transporta</label>
              <input type="text" className={classes.itemBorder}
                {...register("deliveryPriceRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>kol. ostalo</label>
              <input type="text" className={classes.itemBorder}
                {...register("quantityAmountRemainingRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>ukupno</label>
              <input type="text" className={classes.itemBorder}
                {...register("totalValueRange", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>datum ulaza</label>
              <div className="datepickerWrapper">
                <DatePicker
                  selectsRange={true}
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  showMonthDropdown
                  showYearDropdown
                  onChange={(date: any) => {
                    setDateRange([date[0], date[1]]);
                    setValue("dateFrom", date[0] && FormatDateIso(date[0]));
                    setValue("dateTo", date[1] && FormatDateIso(date[1]));
                  }}
                  isClearable={true}
                  className={`${classes.dateItem} ${classes.itemBorder}`}
                />
              </div>
            </div>
          </div>

          <div className={classes.row}>
            <div className={`${classes.col} ${classes.rowDir}`}>
              <label className={classes.mr5}>napomena</label>
              <input type="text" className={classes.itemBorder}
                {...register("note", { required: false })} />
            </div>
          </div>

          <div className={classes.row}>
            <div className={classes.buttons}>
              <label
                onClick={() => switchSearchMode(SearchType.BASIC)}
                className={classes.clickableText}
              >
                PAMETNA PRETRAGA
              </label>
              <button className={classes.iconButton} type="submit">
                <FontAwesomeIcon
                  icon={faLongArrowAltDown}
                  className={classes.icon} />
              </button>
            </div>
          </div>
        </>
      )}
    </form>
  );
};

export default SearchPopover;
